import React, { useState, useRef, useEffect } from "react";
import { useNotifications, AppNotification } from "../../context/NotificationContext";

const EVENT_LABELS: Record<string, string> = {
  "order.new": "New Order",
  "order.fulfilled": "Order Fulfilled",
  "order.tracking_added": "Tracking Added",
  "system.health_check": "Health Check",
  "shift.started": "Shift Started",
  "shift.ended": "Shift Ended",
  "lead.created": "New Lead",
  "inventory.alert": "Inventory Alert",
  "pricing.change_detected": "Price Change",
  "employee.check_in": "Employee Check-In",
  "employee.tactical_delay": "Late Arrival",
  "automation.suggestion": "Automation",
  "shopify.sync_completed": "Shopify Sync",
  "scheduling.task_overdue": "Task Overdue",
  "reporting.generated": "Report Ready",
};

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString();
}

const NotificationCenter: React.FC = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    connected,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        className="notification-bell"
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        <span
          className={`notification-dot ${connected ? "notification-dot--connected" : "notification-dot--disconnected"}`}
        />
      </button>

      {open && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <h3>Notifications</h3>
            <div className="notification-panel-actions">
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="panel-action-btn">
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} className="panel-action-btn">
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : (
              notifications.map((n: AppNotification) => (
                <div
                  key={n.id}
                  className={`notification-item notification-item--${n.notification_type} ${n.read ? "" : "notification-item--unread"}`}
                  onClick={() => markAsRead(n.id)}
                >
                  <div className="notification-item-header">
                    <span className="notification-item-label">
                      {EVENT_LABELS[n.event_type] || n.event_type}
                    </span>
                    <span className="notification-item-time">
                      {formatTime(n.timestamp)}
                    </span>
                  </div>
                  <div className="notification-item-title">{n.title}</div>
                  <div className="notification-item-message">{n.message}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
